import { createEffect, untrack } from "solid-js";
import { createSortable } from "@/lib/dnd";
import type { Todo } from "@listto/powersync";
import { checkIsOverdue, cn, type ListWithMeta } from "@listto/shared";
import { GripVertical } from "@fleet/icons";
import { GlassSurface } from "@fleet/ui";
import { type FocusSlide, playFocusSlideAnimation } from "../hooks/use-drawer-swipe-nav";
import { useTodoSwipeGestures } from "../hooks/use-todo-swipe-gestures";
import {
  OPTIMISTIC_TIMEOUT_MS,
  optimisticCompleted,
  optimisticImportant,
  setOptimisticCompleted,
  setOptimisticImportant,
} from "./todo-item-optimistic";
import {
  TodoItemCheckboxButton,
  TodoItemStarButton,
  TodoItemUnderlay,
  TodoKeyboardHints,
  TodoMetadataBadges,
} from "./todo-item-parts";

const STAGGER_DELAY_MS = 15;
export const MAX_STAGGER_DELAY_MS = 150;

interface TodoItemProps {
  todo: Todo;
  onToggle: (todo: Todo) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
  isSelectionMode: boolean;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  listId: string;
  lists?: ListWithMeta[];
  isInitialLoad?: boolean;
  index: number;
  isExiting?: boolean;
  isNew?: boolean;
  isKeyboardFocused?: boolean;
  onFocus?: (id: string) => void;
  focusSlide?: FocusSlide | null;
}

export function TodoItem(props: TodoItemProps) {
  const isCompleted = () => optimisticCompleted[props.todo.id] ?? props.todo.completed === 1;
  const isImportant = () => optimisticImportant[props.todo.id] ?? props.todo.important === 1;

  let surfaceRef: HTMLDivElement | undefined = undefined;

  // ── Gestos de Deslizar (Swipe-to-Action) ──
  const {
    tx,
    isAnimating,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    getHasSwiped,
    resetHasSwiped,
    COMMIT_THRESHOLD_PX,
  } = useTodoSwipeGestures({
    todo: () => props.todo,
    isCompleted,
    isSelectionMode: () => props.isSelectionMode,
    onToggle: (todo) => {
      props.onToggle(todo);
    },
    onDelete: (id) => {
      props.onDelete(id);
    },
    getSurfaceRef: () => surfaceRef,
    setOptimisticCompleted,
    optimisticTimeoutMs: OPTIMISTIC_TIMEOUT_MS,
  });

  createEffect(() => {
    const completedVal = props.todo.completed === 1;
    const importantVal = props.todo.important === 1;

    // Clean up optimistic states when they match the actual database states
    if (optimisticCompleted[props.todo.id] === completedVal) {
      setOptimisticCompleted(props.todo.id, undefined);
    }
    if (optimisticImportant[props.todo.id] === importantVal) {
      setOptimisticImportant(props.todo.id, undefined);
    }
  });

  const sortable = createSortable(untrack(() => `todo-${props.todo.id}`));
  const isDragging = () => sortable.isActiveDraggable;

  let lastPlayedFocusGeneration = 0;

  createEffect(() => {
    const slide = props.focusSlide;
    if (!slide || !props.isKeyboardFocused) {
      return;
    }
    if (slide.generation === lastPlayedFocusGeneration) {
      return;
    }
    lastPlayedFocusGeneration = slide.generation;
    playFocusSlideAnimation(surfaceRef, slide.direction);
  });

  const isOverdue = () => checkIsOverdue(props.todo.due_date, isCompleted());
  const listName = () => props.lists?.find((item) => item.id === props.todo.list_id)?.name;
  const hasBadges = () =>
    Boolean(
      props.todo.due_date ??
      props.todo.my_day_date ??
      props.todo.recurrence_rule ??
      (props.listId === "all" || props.listId === "planned" ? listName() : null),
    );

  const getAnimationDelay = () => {
    if (props.isNew) {
      return "0ms";
    }
    if (props.isInitialLoad) {
      return `${Math.min(props.index * STAGGER_DELAY_MS, MAX_STAGGER_DELAY_MS)}ms`;
    }
    return undefined;
  };

  return (
    <div id={`todo-item-${props.todo.id}`} ref={sortable.ref} class="px-0.5" data-no-swipe="true">
      <div
        class={cn(
          "will-change-[transform,opacity] transition-[transform,opacity] duration-300",
          props.isExiting ? "opacity-0" : "opacity-100",
          (props.isInitialLoad ?? props.isNew) && "animate-todo-entrance",
          props.isExiting && "animate-todo-exit",
        )}
        style={{
          "animation-delay": getAnimationDelay(),
          "animation-fill-mode": props.isInitialLoad ? "both" : undefined,
        }}
      >
        <div class="relative overflow-hidden rounded-[var(--radius-xl)] touch-pan-y">
          <TodoItemUnderlay
            tx={tx}
            commitThreshold={COMMIT_THRESHOLD_PX}
            isCompleted={isCompleted()}
          />

          <GlassSurface
            ref={(el) => {
              surfaceRef = el;
            }}
            tabindex={0}
            borderEdge="none"
            class={cn(
              "group flex flex-col justify-center px-4 transition-all duration-200 ease-out active:scale-[0.985] touch-manipulation min-h-[56px] overflow-hidden relative cursor-pointer outline-none focus:outline-none z-10 mx-px",
              props.isSelectionMode ? "hover:border-primary/50 shadow-sm" : "hover:bg-muted/10",
              props.isSelected &&
                "border-primary bg-primary/10 shadow-lg ring-1 ring-inset ring-primary/20",
              props.isKeyboardFocused &&
                "ring-2 ring-inset ring-primary bg-primary/5 border-primary shadow-md scale-[1.005]",
              isDragging() &&
                "opacity-40 scale-[1.02] shadow-2xl z-50 ring-2 ring-inset ring-primary",
            )}
            style={{
              transform: `translate3d(${tx()}px, 0, 0)`,
              transition: isAnimating() ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)" : "none",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onFocus={() => props.onFocus?.(props.todo.id)}
            onClick={(e) => {
              if (getHasSwiped()) {
                resetHasSwiped();
                return;
              }
              if (
                e.target instanceof HTMLElement &&
                (e.target.closest("button") || e.target.closest("a"))
              ) {
                return;
              }
              if (props.isSelectionMode) {
                props.onToggleSelection(props.todo.id);
              } else {
                props.onSelect(props.todo.id);
              }
            }}
            onKeyDown={(e) => {
              if (
                e.target instanceof HTMLElement &&
                (e.target.closest("button") || e.target.closest("a"))
              ) {
                return;
              }
              if (e.key === "Enter" || e.key === " ") {
                if (props.isSelectionMode) {
                  props.onToggleSelection(props.todo.id);
                } else {
                  props.onSelect(props.todo.id);
                }
              }
            }}
          >
            {/* Drag Handle (Desktop Only) */}
            <div
              class="absolute left-1.5 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex items-center justify-center rounded-md hover:bg-muted/20"
              {...sortable.dragActivators}
              data-no-todo-swipe="true"
            >
              <GripVertical class="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60" />
            </div>

            <div class="flex items-center gap-3.5 lg:pl-4">
              <TodoItemCheckboxButton
                todo={props.todo}
                isSelectionMode={props.isSelectionMode}
                isSelected={props.isSelected}
                isCompleted={isCompleted()}
                onToggleSelection={props.onToggleSelection}
                onToggle={props.onToggle}
              />

              {/* Content Area */}
              <div class="flex-1 min-w-0 flex flex-col justify-center py-1">
                <p
                  class={cn(
                    "text-[14px] font-medium leading-tight transition-all duration-200 line-clamp-2 break-words",
                    isCompleted()
                      ? "line-through text-muted-foreground/40 italic"
                      : "text-foreground group-hover:text-primary",
                  )}
                >
                  {props.todo.text}
                </p>

                {/* Metadata Badges */}
                <TodoMetadataBadges
                  todo={props.todo}
                  listId={props.listId}
                  listName={listName() ?? undefined}
                  isOverdue={isOverdue()}
                  hasBadges={hasBadges()}
                />
              </div>

              {/* Actions (Important / Trash) */}
              <div class="flex items-center gap-2.5 shrink-0">
                <TodoKeyboardHints isKeyboardFocused={props.isKeyboardFocused} />
                <TodoItemStarButton todo={props.todo} isImportant={isImportant()} />
              </div>
            </div>

            {/* Interactive Overlay (Hover feedback) */}
            <div class="absolute inset-0 bg-foreground/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
          </GlassSurface>
        </div>
      </div>
    </div>
  );
}
